import React, { useCallback, useContext, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDetailReports, getListDevice, getListProduct } from '../services/api/account.service';
import { getLocations } from '../services/api/locations.services';
import { showApiError } from '../services/apiError';
import ModulesContext, { MODULE_PERMISSION_CACHE_KEY } from '../context/ModulesContext';
import { DropdownSearch } from '../component/DropdownSearch';
import { FilterBar, FilterField, IconCalendar, IconLayers, IconPin, IconReceipt, IconTag, filterControlStyle } from '../component/FilterField';
import { useResponsive } from '../hook/useResponsive';
import ListFilterBar from '../component/list/ListFilterBar';
import ListMobile from '../component/list/ListMobile';
import ListTable from '../component/list/ListTable';
import {
    Density,
    Icon,
    ListColumn,
    SortDir,
    TEAL,
    TEXT,
    cells,
    formatDayMonth,
    formatMoney,
    formatUpdatedAt,
    t,
    toLocalDay,
} from '../component/list/listShared';

const toHeaderName = (key: string) =>
    key
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
// Native date inputs speak yyyy-mm-dd in local time; the filter keeps ISO instants, and
// the To bound is the NEXT local midnight because the reporting window is half-open.
const toDateInput = (iso: string | null, exclusiveEnd = false) => {
    if (!iso) return '';
    const d = new Date(new Date(iso).getTime() - (exclusiveEnd ? 1 : 0));
    return `${String(d.getFullYear()).padStart(4, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fromDateInput = (value: string, exclusiveEnd = false) => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    const dt = new Date(0);
    dt.setFullYear(y, m - 1, exclusiveEnd ? d + 1 : d);
    dt.setHours(0, 0, 0, 0);
    return dt.toISOString();
};

type ReportRow = Record<string, any>;
type ColumnKind = 'money' | 'qty' | 'number' | 'timestamp' | 'day' | 'text';
interface ReportColumn extends ListColumn<ReportRow> {
    kind: ColumnKind;
    /** Display text: mobile subtitle/sheet, search and the default cell. */
    text: (row: ReportRow) => string;
}

interface ReportFilter {
    dateFrom: string | null;
    dateTo: string | null;
    locationId: string;
    txnType: string;
    movementType: string;
    productId: string;
    deviceId: string;
}

// Core turns '' and a missing param alike into NULL for the proc.
const defaultFilter = (): ReportFilter => ({
    dateFrom: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    dateTo: new Date().toISOString(),
    locationId: '',
    txnType: '',
    movementType: '',
    productId: '',
    deviceId: '',
});

const ROW_KEY = '__rowKey';
const CLEAR_ID = 'clear';
const PICKER_PAGE = 50;
const KIND_SAMPLE = 200;

/* ---------------- Column inference from the proc's row shape ---------------- */

const tokensOf = (key: string) =>
    key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
const ID_WORDS = new Set(['id', 'uuid', 'no', 'number', 'code', 'sku', 'barcode', 'ean', 'ref', 'reference']);
const RATE_WORDS = new Set(['rate', 'percent', 'percentage', 'pct', 'ratio', 'share', 'margin']);
const QTY_WORDS = new Set(['qty', 'quantity', 'quantities', 'count', 'units', 'covers', 'guests', 'stock', 'items', 'pieces', 'pcs']);
const MONEY_WORDS = new Set([
    'amount', 'total', 'price', 'net', 'tax', 'vat', 'cost', 'value', 'revenue', 'sales', 'discount', 'discounts', 'gross',
    'subtotal', 'tip', 'tips', 'gratuity', 'paid', 'payment', 'payments', 'cash', 'card', 'refund', 'refunds',
    'balance', 'fee', 'fees', 'charge', 'profit', 'float', 'change', 'variance',
]);
// The trailing amount on a phone card: the first of these a money key contains wins.
const AMOUNT_RANK = ['gross', 'total', 'amount', 'revenue', 'sales', 'net'];

const NUMERIC = /^-?\d+(\.\d+)?$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.0+)?Z)?$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isIdKey = (key: string) => {
    const tokens = tokensOf(key);
    return tokens.length > 0 && ID_WORDS.has(tokens[tokens.length - 1]);
};
const isNumeric = (v: any) =>
    typeof v === 'number' ? Number.isFinite(v) : typeof v === 'string' && NUMERIC.test(v.trim());

// The head noun comes last in snake_case keys (stock_value, sales_count), so scan from the end.
const numericKindOf = (key: string): ColumnKind => {
    for (const token of [...tokensOf(key)].reverse()) {
        if (RATE_WORDS.has(token)) return 'number';
        if (QTY_WORDS.has(token)) return 'qty';
        if (MONEY_WORDS.has(token)) return 'money';
    }
    return 'number';
};

const kindOf = (key: string, rows: ReportRow[]): ColumnKind => {
    const filled = rows.slice(0, KIND_SAMPLE).map(r => r[key]).filter(v => v !== null && v !== undefined && v !== '');
    if (filled.length === 0 || isIdKey(key)) return 'text';
    if (filled.every(isNumeric)) return numericKindOf(key);
    if (filled.every(v => typeof v === 'string' && ISO_DAY.test(v))) return 'day';
    if (filled.every(v => typeof v === 'string' && ISO_TIMESTAMP.test(v) && !Number.isNaN(Date.parse(v)))) {
        return 'timestamp';
    }
    return 'text';
};

const amountRank = (key: string) => {
    const tokens = tokensOf(key);
    if (tokens.some(w => w === 'tax' || w === 'vat' || w === 'discount')) return AMOUNT_RANK.length + 1;
    const index = AMOUNT_RANK.findIndex(w => tokens.includes(w));
    return index === -1 ? AMOUNT_RANK.length : index;
};

const rawText = (v: any): string =>
    v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
const toNumber = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const formatQty = (v: number | null) =>
    v === null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
// A calendar day is built from its parts, never parsed as a UTC instant.
const formatDay = (v: any) => {
    const [y, m, d] = String(v ?? '').slice(0, 10).split('-').map(Number);
    return y && m && d ? formatDayMonth(new Date(y, m - 1, d)) : rawText(v);
};

const displayText = (kind: ColumnKind, v: any, language: any): string => {
    switch (kind) {
        case 'money':
            return formatMoney(toNumber(v));
        case 'qty':
            return formatQty(toNumber(v));
        case 'timestamp':
            return formatUpdatedAt(v, language);
        case 'day':
            return formatDay(v);
        default:
            return rawText(v);
    }
};

const BASE_WIDTH: Record<ColumnKind, number> = { money: 120, qty: 100, number: 110, timestamp: 150, day: 120, text: 180 };
const widthOf = (kind: ColumnKind, label: string, isTitle: boolean) =>
    Math.min(260, Math.max(isTitle ? 220 : BASE_WIDTH[kind], Math.round(label.length * 7.5) + 24));

const numberCell = (text: (row: ReportRow) => string) => (row: ReportRow) => (
    <div
        className="font-inter"
        style={{
            fontSize: '14px',
            color: TEXT,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }}
    >
        {text(row)}
    </div>
);

const buildColumns = (rows: ReportRow[], language: any): ReportColumn[] => {
    if (rows.length === 0) return [];
    const keys = Object.keys(rows[0]).filter(k => k !== ROW_KEY);
    const kindByKey: Record<string, ColumnKind> = {};
    keys.forEach(k => {
        kindByKey[k] = kindOf(k, rows);
    });
    // Ids and flags (by name or by value: started_by holds UUIDs, voided is a boolean) never title a phone card.
    const idLike = (k: string) =>
        isIdKey(k) ||
        rows.slice(0, KIND_SAMPLE).every(r => r[k] === null || r[k] === undefined || typeof r[k] === 'boolean' || UUID.test(String(r[k])));
    const cardText = keys.filter(k => kindByKey[k] === 'text' && !idLike(k));
    const dateKey = keys.find(k => kindByKey[k] === 'timestamp' || kindByKey[k] === 'day');
    const titleKey = cardText[0] ?? dateKey ?? keys[0];
    const subtitleKey = cardText.find(k => k !== titleKey) ?? (dateKey !== titleKey ? dateKey : undefined);
    const moneyKeys = keys.filter(k => kindByKey[k] === 'money');
    const trailingKey = moneyKeys.length
        ? moneyKeys.reduce((best, k) => (amountRank(k) < amountRank(best) ? k : best))
        : keys.find(k => kindByKey[k] === 'qty' && k !== titleKey);

    return keys.map((key, index) => {
        const kind = kindByKey[key];
        const label: string = language?.[key] ?? toHeaderName(key);
        const text = (row: ReportRow) => displayText(kind, row[key], language);
        const column: ReportColumn = {
            key,
            kind,
            label,
            text,
            value: text,
            width: widthOf(kind, label, key === titleKey),
            sortable: true,
            hideable: index !== 0,
            align: kind === 'money' || kind === 'qty' || kind === 'number' ? 'right' : undefined,
            mobile:
                key === titleKey ? 'title' : key === subtitleKey ? 'subtitle' : key === trailingKey ? 'trailing' : 'sheet',
        };
        if (kind === 'money') column.render = cells.money((row: ReportRow) => toNumber(row[key]), key === trailingKey);
        else if (kind === 'qty' || kind === 'number') column.render = numberCell(text);
        else if (kind === 'timestamp') column.render = cells.date((row: ReportRow) => row[key]);
        return column;
    });
};

const sortValueOf = (kind: ColumnKind, v: any): number | string | null => {
    if (v === null || v === undefined || v === '') return null;
    if (kind === 'money' || kind === 'qty' || kind === 'number') return toNumber(v);
    if (kind === 'timestamp') {
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? null : ms;
    }
    return rawText(v);
};

/* ---------------- CSV export ---------------- */

// Excel splits a double-clicked .csv on the system list separator: ';' where the decimal mark is ','.
const csvFormat = () => {
    const commaDecimal =
        (1.5).toLocaleString(navigator.language).includes(',') || localStorage.getItem('language') === 'al';
    return { sep: commaDecimal ? ';' : ',', decimal: commaDecimal ? ',' : '.' };
};
const csvCell = (value: string, sep: string) => {
    // A leading = + - @ makes spreadsheets run the cell as a formula; numbers stay numbers.
    const safe = /^[=+\-@\t\r]/.test(value) && !/^-?\d+([.,]\d+)?$/.test(value) ? `'${value}` : value;
    return safe.includes(sep) || /["\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};
const csvValue = (kind: ColumnKind, v: any, decimal: string): string => {
    if (v === null || v === undefined) return '';
    if ((kind === 'money' || kind === 'qty' || kind === 'number') && isNumeric(v)) return String(v).trim().replace('.', decimal);
    if (kind === 'day') return String(v).slice(0, 10);
    if (kind === 'timestamp') {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return rawText(v);
        return `${toLocalDay(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return rawText(v);
};
const downloadCsv = (fileName: string, lines: string[]) => {
    // BOM so Excel reads UTF-8 (ë, ç) instead of the system code page.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/* ---------------- Report title from the menu ---------------- */

// DashboardLayout builds the report menu from GET /api/reports as { name, path: /report/:id }.
const reportNameIn = (modules: any, id: string): string => {
    if (!Array.isArray(modules)) return '';
    for (const section of modules) {
        const items = Array.isArray(section?.value) ? section.value : [];
        const hit = items.find((item: any) => item?.path === `/report/${id}`);
        if (hit?.name) return String(hit.name);
    }
    return '';
};
const cachedModules = (): any => {
    try {
        return JSON.parse(localStorage.getItem(MODULE_PERMISSION_CACHE_KEY) || '[]');
    } catch {
        return [];
    }
};

/* ---------------- Paged option lists (Product, Till) ---------------- */

const usePagedPicker = <T,>(fetchPage: (page: number, search: string) => Promise<T[]>) => {
    const fetchRef = useRef(fetchPage);
    fetchRef.current = fetchPage;
    const [items, setItems] = useState<T[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const state = useRef({ seq: 0, busy: false, hasMore: false, page: 1, search: '' });

    const load = useCallback(async (search: string, append: boolean) => {
        const s = state.current;
        const page = append ? s.page : 1;
        const run = ++s.seq;
        s.busy = true;
        setLoading(true);
        try {
            const list = await fetchRef.current(page, search);
            if (run !== s.seq) return;
            setItems(prev => (append ? [...prev, ...list] : list));
            s.page = page + 1;
            s.search = search;
            s.hasMore = list.length >= PICKER_PAGE;
        } catch (error) {
            if (run !== s.seq) return;
            console.error('Error loading filter options:', error);
            if (!append) setItems([]);
            s.hasMore = false;
        } finally {
            if (run === s.seq) {
                s.busy = false;
                setHasMore(s.hasMore);
                setLoading(false);
            }
        }
    }, []);

    const search = useCallback((term: string) => load(term.trim(), false), [load]);
    const loadMore = useCallback(() => {
        if (!state.current.busy && state.current.hasMore) load(state.current.search, true);
    }, [load]);
    return { items, hasMore, loading, search, loadMore };
};

const SalesReportsGrid = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { modules } = useContext(ModulesContext);
    const { windowWidth } = useResponsive();
    // The list kit switches below 1024px; the host hook's isMobile is <= 1024.
    const isMobile = windowWidth < 1024;
    const [language, setLanguage] = useState<any>(() => window.languageContext?.language ?? null);
    const languageRef = useRef<any>(language);
    languageRef.current = language;

    const title = useMemo(
        () => reportNameIn(modules, id) || reportNameIn(cachedModules(), id) || localStorage.getItem('title') || '',
        [modules, id]
    );
    const showType = title === 'Inventory Movements' || title === 'Sales Details';
    const showProduct = title === 'Product Performance';
    const showTill = title === 'X Reports' || title === 'Z Reports';
    const titleText = t(language, title || 'Sales Reports', title || 'Sales Reports');
    const clearLabel = t(language, 'Remove select', 'Remove select');

    const [filter, setFilter] = useState<ReportFilter>(defaultFilter);
    const filterRef = useRef<ReportFilter>(filter);
    const committedDatesRef = useRef('');
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const requestSeq = useRef(0);
    const loadedFilterRef = useRef<ReportFilter>(filter);

    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [sortBy, setSortBy] = useState('');
    const [sortDir, setSortDir] = useState<SortDir>('DESC');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [density, setDensity] = useState<Density>('default');
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

    const [locations, setLocations] = useState<any[]>([]);
    const [searchLocation, setSearchLocation] = useState('');
    const [isLocationOpen, setIsLocationOpen] = useState(false);
    const locationRef = useRef<HTMLDivElement>(null);
    const [selectedType, setSelectedType] = useState('');
    const [isTypeOpen, setIsTypeOpen] = useState(false);
    const typeRef = useRef<HTMLDivElement>(null);
    const [searchProduct, setSearchProduct] = useState('');
    const [isProductOpen, setIsProductOpen] = useState(false);
    const productRef = useRef<HTMLDivElement>(null);
    const [searchDevice, setSearchDevice] = useState('');
    const [isDeviceOpen, setIsDeviceOpen] = useState(false);
    const deviceRef = useRef<HTMLDivElement>(null);

    const {
        items: products,
        hasMore: hasMoreProducts,
        loading: loadingProducts,
        search: searchProducts,
        loadMore: loadMoreProducts,
    } = usePagedPicker<{ id: string; productName: string }>(async (pageNo, term) => {
        const response: any = await getListProduct(pageNo, PICKER_PAGE, term, localStorage.getItem('companyId') || '');
        const list = response?.data?.productWithCompany;
        return Array.isArray(list) ? list : [];
    });
    const {
        items: devices,
        hasMore: hasMoreDevices,
        loading: loadingDevices,
        search: searchDevices,
        loadMore: loadMoreDevices,
    } = usePagedPicker<{ id: string; deviceName: string }>(async (pageNo, term) => {
        const response: any = await getListDevice(pageNo, PICKER_PAGE, term);
        const list = response?.data?.data?.devices;
        return Array.isArray(list) ? list : [];
    });

    useEffect(() => {
        const refresh = () => {
            if (window.languageContext) setLanguage(window.languageContext.language);
        };
        refresh();
        document.addEventListener('languageLoaded', refresh);
        return () => document.removeEventListener('languageLoaded', refresh);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const response: any = await getLocations(localStorage.getItem('userId') || '', localStorage.getItem('companyId') || '', '', '', '');
                setLocations(Array.isArray(response?.data?.data) ? response.data.data : []);
            } catch (error) {
                console.error('Error loading locations:', error);
            }
        })();
    }, []);

    // Latest wins: a slower, older response never replaces the rows of a newer request.
    const fetchReport = useCallback(async (reportId: string, next: ReportFilter) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        try {
            const response: any = await getDetailReports(
                reportId,
                1,
                50,
                next.dateFrom,
                next.dateTo,
                next.locationId,
                next.txnType,
                next.movementType,
                next.productId,
                next.deviceId
            );
            if (seq !== requestSeq.current) return;
            const list = response?.data?.data?.rows;
            setRows(Array.isArray(list) ? list.map((row: ReportRow, index: number) => ({ ...row, [ROW_KEY]: String(index) })) : []);
            setFailed(false);
            setPage(1);
            loadedFilterRef.current = next;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            setRows([]);
            setFailed(true);
            showApiError(error, {
                language: languageRef.current,
                titleFallback: t(languageRef.current, 'Could not load the report', 'Could not load the report'),
            });
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, []);

    // A new report starts from the defaults: no filter, search or sort carries over.
    useEffect(() => {
        const fresh = defaultFilter();
        filterRef.current = fresh;
        committedDatesRef.current = `${fresh.dateFrom}|${fresh.dateTo}`;
        setFilter(fresh);
        setRows([]);
        setFailed(false);
        setSearchLocation('');
        setSelectedType('');
        setSearchProduct('');
        setSearchDevice('');
        setSearch('');
        setSortBy('');
        setSortDir('DESC');
        setPage(1);
        setVisibleColumns({});
        fetchReport(id, fresh);
    }, [id, fetchReport]);

    useEffect(() => {
        if (showProduct) searchProducts('');
    }, [id, showProduct, searchProducts]);
    useEffect(() => {
        if (showTill) searchDevices('');
    }, [id, showTill, searchDevices]);

    const updateFilter = (patch: Partial<ReportFilter>) => {
        filterRef.current = { ...filterRef.current, ...patch };
        setFilter(filterRef.current);
    };
    const applyFilter = (patch: Partial<ReportFilter>) => {
        updateFilter(patch);
        fetchReport(id, filterRef.current);
    };

    // Typing fires change per keystroke, so the reload waits for blur / Enter and skips unchanged dates.
    const handleDateChange = (key: 'dateFrom' | 'dateTo', value: string) => {
        updateFilter({ [key]: fromDateInput(value, key === 'dateTo') });
    };
    const commitDates = () => {
        const current = filterRef.current;
        if (!current.dateFrom || !current.dateTo) return;
        const key = `${current.dateFrom}|${current.dateTo}`;
        if (key === committedDatesRef.current) return;
        committedDatesRef.current = key;
        fetchReport(id, current);
    };

    const handleSelectLocation = (item: any) => {
        const cleared = item.id === CLEAR_ID;
        setSearchLocation(cleared ? '' : item.name);
        setIsLocationOpen(false);
        applyFilter({ locationId: cleared ? '' : item.id });
    };
    const handleSelectType = (item: any) => {
        const cleared = item.id === CLEAR_ID;
        setSelectedType(cleared ? '' : String(item.name));
        setIsTypeOpen(false);
        const value = cleared ? '' : String(item.value);
        applyFilter(title === 'Sales Details' ? { txnType: value } : { movementType: value });
    };
    const handleSelectProduct = (item: any) => {
        const cleared = item.id === CLEAR_ID;
        setSearchProduct(cleared ? '' : item.productName);
        setIsProductOpen(false);
        applyFilter({ productId: cleared ? '' : item.id });
    };
    const handleSelectDevice = (item: any) => {
        const cleared = item.id === CLEAR_ID;
        setSearchDevice(cleared ? '' : item.deviceName);
        setIsDeviceOpen(false);
        applyFilter({ deviceId: cleared ? '' : item.id });
    };
    // DropdownSearch writes the picked label back; the clear row must leave the box empty.
    const unlessClear = (set: (v: string) => void) => (v: string) => set(v === clearLabel ? '' : v);

    /* ---------------- Rows: search, sort, page (all in memory) ---------------- */

    const columns = useMemo(() => buildColumns(rows, language), [rows, language]);
    const haystacks = useMemo(
        () => rows.map(row => columns.map(c => `${rawText(row[c.key])}\u0001${c.text(row)}`).join('\u0001').toLowerCase()),
        [rows, columns]
    );
    const term = deferredSearch.trim().toLowerCase();
    const filteredRows = useMemo(
        () => (term ? rows.filter((_, index) => haystacks[index].includes(term)) : rows),
        [rows, haystacks, term]
    );
    const sortedRows = useMemo(() => {
        const column = columns.find(c => c.key === sortBy);
        if (!column) return filteredRows;
        const dir = sortDir === 'ASC' ? 1 : -1;
        return filteredRows
            .map(row => ({ row, v: sortValueOf(column.kind, row[column.key]) }))
            .sort((a, b) => {
                if (a.v === null || b.v === null) return a.v === b.v ? 0 : a.v === null ? 1 : -1;
                const diff =
                    typeof a.v === 'number' && typeof b.v === 'number'
                        ? a.v - b.v
                        : String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: 'base' });
                return diff * dir || Number(a.row[ROW_KEY]) - Number(b.row[ROW_KEY]);
            })
            .map(entry => entry.row);
    }, [filteredRows, columns, sortBy, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sortedRows.length / limit));
    const currentPage = Math.min(page, totalPages);
    const pageRows = sortedRows.slice((currentPage - 1) * limit, currentPage * limit);
    const rowKey = (row: ReportRow) => String(row[ROW_KEY]);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };
    const handleSortChange = (nextBy: string, nextDir: SortDir) => {
        setSortBy(nextBy);
        setSortDir(nextDir);
        setPage(1);
    };

    // After a failed load the list is empty on purpose; say so instead of "no records".
    const emptyState = failed
        ? {
            title: t(language, 'Could not load the report', 'Could not load the report'),
            desc: t(language, 'Change a filter to try again.', 'Change a filter to try again.'),
        }
        : {
            title: t(language, 'No Records Found', 'No Records Found'),
            desc: t(language, 'No records match the selected filters', 'No records match the selected filters'),
        };
    // A failed load shows its own empty state, not "no results for your search".
    const hasActiveQuery = !failed && term !== '';

    const exportColumns = columns.filter(c => c.hideable === false || visibleColumns[c.key] !== false);
    const canExport = !loading && sortedRows.length > 0 && exportColumns.length > 0;
    const exportCsv = () => {
        if (!canExport) return;
        const { sep, decimal } = csvFormat();
        const header = exportColumns.map(c => csvCell(t(language, c.label, c.label), sep));
        const body = sortedRows.map(row => exportColumns.map(c => csvCell(csvValue(c.kind, row[c.key], decimal), sep)).join(sep));
        const loaded = loadedFilterRef.current;
        const range = `${toDateInput(loaded.dateFrom)} to ${toDateInput(loaded.dateTo, true)}`;
        const name = (title || 'Report').replace(/[\\/:*?"<>|]+/g, '').trim() || 'Report';
        downloadCsv(`${name} ${range}.csv`, [header.join(sep), ...body]);
    };

    // The pill is the tap target on a phone: 28px control + 16px padding = 44px.
    const controlHeight = isMobile ? 28 : 20;
    const typeOptions =
        title === 'Inventory Movements'
            ? [
                { id: CLEAR_ID, name: clearLabel },
                { value: 1, name: t(language, 'Sales', 'Sales') },
                { value: 2, name: t(language, 'Purchase', 'Purchase') },
                { value: 3, name: t(language, 'Refund', 'Refund') },
                { value: 4, name: t(language, 'Correction', 'Correction') },
                { value: 5, name: t(language, 'TransferOut', 'TransferOut') },
                { value: 6, name: t(language, 'Adjustment', 'Adjustment') },
                { value: 7, name: t(language, 'TransferIn', 'TransferIn') },
            ]
            : [
                { id: CLEAR_ID, name: clearLabel },
                { value: 1, name: t(language, 'Sales', 'Sales') },
                { value: 2, name: t(language, 'Refund', 'Refund') },
            ];

    return (
        // Below 640px every input is 16px so iOS Safari does not zoom on focus.
        <div
            className="h-full max-lg:[&_input]:!text-[16px]"
            style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', paddingBottom: isMobile ? 24 : undefined }}
        >
            <div className="flex items-center justify-between gap-3" style={{ flexShrink: 0 }}>
                <h1
                    style={{ color: '#222525', minWidth: 0 }}
                    className="text-[24px] max-sm:text-[20px] leading-[130%] font-medium font-inter truncate"
                >
                    {titleText}
                </h1>
                <button
                    type="button"
                    onClick={exportCsv}
                    disabled={!canExport}
                    title={t(language, 'Export the rows shown as CSV', 'Export the rows shown as CSV')}
                    className="inline-flex flex-none items-center gap-2 h-9 max-lg:h-10 px-3.5 rounded-[10px] border border-[#C9D7D8] bg-white font-inter text-[13px] font-medium enabled:hover:bg-[#EBF9F9] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ color: TEAL }}
                >
                    <Icon name="arrowd" color={TEAL} size={16} />
                    {t(language, 'Export', 'Export')} CSV
                </button>
            </div>

            <div style={{ marginTop: 24, flexShrink: 0 }}>
                <FilterBar mobile={isMobile}>
                    <FilterField label={t(language, 'From', 'From')} icon={<IconCalendar />} fluid={isMobile}>
                        <input
                            type="date"
                            value={toDateInput(filter.dateFrom)}
                            max={toDateInput(filter.dateTo, true) || undefined}
                            onChange={e => handleDateChange('dateFrom', e.target.value)}
                            onBlur={commitDates}
                            onKeyDown={e => e.key === 'Enter' && commitDates()}
                            className="font-inter"
                            style={filterControlStyle(isMobile)}
                        />
                    </FilterField>
                    <FilterField label={t(language, 'To', 'To')} icon={<IconCalendar />} fluid={isMobile}>
                        <input
                            type="date"
                            value={toDateInput(filter.dateTo, true)}
                            min={toDateInput(filter.dateFrom) || undefined}
                            onChange={e => handleDateChange('dateTo', e.target.value)}
                            onBlur={commitDates}
                            onKeyDown={e => e.key === 'Enter' && commitDates()}
                            className="font-inter"
                            style={filterControlStyle(isMobile)}
                        />
                    </FilterField>
                    <FilterField label={t(language, 'Location', 'Location')} icon={<IconPin />} fluid={isMobile} as="div">
                        <div style={{ flex: 1, minWidth: isMobile ? 0 : 180, height: controlHeight }}>
                            <DropdownSearch
                                refKey="location"
                                refs={{ location: locationRef }}
                                openStates={{ location: isLocationOpen }}
                                open={() => setIsLocationOpen(true)}
                                close={() => setIsLocationOpen(false)}
                                data={[{ id: CLEAR_ID, name: clearLabel }, ...locations]}
                                labelExtractor={(item: any) => item?.name ?? ''}
                                value={searchLocation}
                                setValue={unlessClear(setSearchLocation)}
                                onSelect={handleSelectLocation}
                                placeholder={t(language, 'All locations', 'All locations')}
                                ariaLabel={t(language, 'Location', 'Location')}
                                borderNone
                            />
                        </div>
                    </FilterField>

                    {showType && (
                        <FilterField label={t(language, 'Type', 'Type')} icon={<IconLayers />} fluid={isMobile} as="div">
                            <div style={{ flex: 1, minWidth: isMobile ? 0 : 180, height: controlHeight }}>
                                <DropdownSearch
                                    refKey="type"
                                    refs={{ type: typeRef }}
                                    openStates={{ type: isTypeOpen }}
                                    open={() => setIsTypeOpen(true)}
                                    close={() => setIsTypeOpen(false)}
                                    data={typeOptions}
                                    labelExtractor={(item: any) => item.name}
                                    value={selectedType}
                                    setValue={unlessClear(setSelectedType)}
                                    onSelect={handleSelectType}
                                    placeholder={t(language, 'All types', 'All types')}
                                    ariaLabel={t(language, 'Type', 'Type')}
                                    borderNone
                                />
                            </div>
                        </FilterField>
                    )}

                    {showProduct && (
                        <FilterField label={t(language, 'Product', 'Product')} icon={<IconTag />} fluid={isMobile} as="div">
                            <div style={{ flex: 1, minWidth: isMobile ? 0 : 200, height: controlHeight }}>
                                <DropdownSearch<{ productName: string; id: string }>
                                    refKey="product"
                                    refs={{ product: productRef }}
                                    openStates={{ product: isProductOpen }}
                                    open={() => setIsProductOpen(true)}
                                    close={() => setIsProductOpen(false)}
                                    data={[{ id: CLEAR_ID, productName: clearLabel }, ...products]}
                                    labelExtractor={item => item.productName}
                                    value={searchProduct}
                                    setValue={unlessClear(setSearchProduct)}
                                    onSelect={handleSelectProduct}
                                    onSearch={searchProducts}
                                    onLoadMore={loadMoreProducts}
                                    hasMore={hasMoreProducts}
                                    loading={loadingProducts}
                                    placeholder={t(language, 'All products', 'All products')}
                                    ariaLabel={t(language, 'Product', 'Product')}
                                    borderNone
                                />
                            </div>
                        </FilterField>
                    )}

                    {showTill && (
                        <FilterField label={t(language, 'Till', 'Till')} icon={<IconReceipt />} fluid={isMobile} as="div">
                            <div style={{ flex: 1, minWidth: isMobile ? 0 : 200, height: controlHeight }}>
                                <DropdownSearch<{ deviceName: string; id: string }>
                                    refKey="device"
                                    refs={{ device: deviceRef }}
                                    openStates={{ device: isDeviceOpen }}
                                    open={() => setIsDeviceOpen(true)}
                                    close={() => setIsDeviceOpen(false)}
                                    data={[{ id: CLEAR_ID, deviceName: clearLabel }, ...devices]}
                                    labelExtractor={item => item.deviceName}
                                    value={searchDevice}
                                    setValue={unlessClear(setSearchDevice)}
                                    onSelect={handleSelectDevice}
                                    onSearch={searchDevices}
                                    onLoadMore={loadMoreDevices}
                                    hasMore={hasMoreDevices}
                                    loading={loadingDevices}
                                    placeholder={t(language, 'All devices', 'All devices')}
                                    ariaLabel={t(language, 'Till', 'Till')}
                                    borderNone
                                />
                            </div>
                        </FilterField>
                    )}
                </FilterBar>
            </div>

            {isMobile ? (
                <div style={{ marginTop: 16, flexShrink: 0 }}>
                    <ListMobile
                        language={language}
                        // The sheet's Clear all goes back to the report's own row order.
                        onClearFilters={() => handleSortChange('', 'DESC')}
                        columns={columns}
                        rows={pageRows}
                        rowKey={rowKey}
                        loading={loading}
                        count={sortedRows.length}
                        page={currentPage}
                        limit={limit}
                        onPageChange={setPage}
                        searchValue={search}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSortChange={handleSortChange}
                        emptyState={emptyState}
                        hasActiveQuery={hasActiveQuery}
                    />
                </div>
            ) : (
                <>
                    <div style={{ marginTop: 16, flexShrink: 0 }}>
                        <ListFilterBar
                            language={language}
                            searchValue={search}
                            onSearchChange={handleSearchChange}
                            searchPlaceholder="Search"
                            columns={columns}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSortChange={handleSortChange}
                            visibleColumns={visibleColumns}
                            onToggleColumn={(key: string) =>
                                setVisibleColumns(prev => ({ ...prev, [key]: prev[key] === false }))
                            }
                            density={density}
                            onDensityChange={setDensity}
                        />
                    </div>
                    <div style={{ marginTop: 14, flex: '1 1 0', minHeight: 320 }}>
                        <ListTable
                            language={language}
                            columns={columns}
                            rows={pageRows}
                            rowKey={rowKey}
                            loading={loading}
                            count={sortedRows.length}
                            page={currentPage}
                            limit={limit}
                            onPageChange={setPage}
                            onLimitChange={(next: number) => {
                                setLimit(next);
                                setPage(1);
                            }}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSortChange={handleSortChange}
                            emptyState={emptyState}
                            hasActiveQuery={hasActiveQuery}
                            visibleColumns={visibleColumns}
                            density={density}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default SalesReportsGrid;
